import { faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Editor } from "@tiptap/core";
import { EditorContent, useEditor } from "@tiptap/react";
import classNames from "classnames";
import { format, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import ReactFlow, {
  Background,
  Controls,
  Edge,
  Node,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "reactflow";
import { format as formatTimeAgo } from "timeago.js";
import useLocalStorageState from "use-local-storage-state";
import { yDocToProsemirrorJSON } from "y-prosemirror";
import * as Y from "yjs";

import { django } from "@/api";
import { loadExtensions } from "@/extensions";
import { useYdoc } from "@/hooks";
import { getPageVersions } from "@/hooks/useQueries/pages";
import { Page, Project, Version } from "@/types";

import GraphEdge from "../Graph/GraphEdge";
import GraphNodeReadOnly from "../Graph/GraphNodeReadOnly";
import { getEditorNodes } from "../Graph/utils";
import styles from "./Versions.module.scss";

const nodeTypes = {
  GraphNode: GraphNodeReadOnly,
  SyncedNode: GraphNodeReadOnly,
};
const edgeTypes = { default: GraphEdge };

export const Versions = ({
  id,
  editor,
  project,
}: {
  id?: string;
  editor: Editor;
  project?: Project;
}) => {
  const [currentVersion, setCurrentVersion] = useState<Version>();
  const [currentVersionYdoc, setCurrentVersionYdoc] = useState<Y.Doc>();
  const [detailLoading, setDetailLoading] = useState<boolean>(true);
  const [currentView, setCurrentView] = useLocalStorageState<string>(`ldg-versionsCurrentView`, {
    defaultValue: "editor",
  });
  const { nodesMap, edgesMap } = useYdoc();
  const extensions = loadExtensions(undefined, undefined, true);
  const readOnlyEditor = useEditor({ editable: false, extensions });
  const queryClient = useQueryClient();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowInstance = useReactFlow();

  const {
    data: versions,
    isLoading,
    isFetched,
  } = useQuery({
    queryKey: ["page-versions", project?.name, id],
    queryFn: ({ signal }) => getPageVersions(signal, `lateral-ldg-${project?.name}-${id}`),
    enabled: Boolean(project?.name && id),
    initialData: [],
    retry: false,
  });

  useEffect(() => {
    if (readOnlyEditor && versions?.length) setCurrentVersion(versions?.[0]);
  }, [readOnlyEditor, isFetched]);

  const loadDetail = async () => {
    setDetailLoading(true);
    const response = await django.get(`/editor/snapshots/${currentVersion?.pk}/crdt`, {
      responseType: "arraybuffer",
    });
    const deserializedYDoc = new Y.Doc();
    Y.applyUpdate(deserializedYDoc, Y.mergeUpdates([new Uint8Array(response.data)]));
    setCurrentVersionYdoc(deserializedYDoc);
    const json = yDocToProsemirrorJSON(deserializedYDoc, "default");
    readOnlyEditor?.commands.setContent(json);

    if (!readOnlyEditor) return;

    const { ids } = getEditorNodes(readOnlyEditor);

    // @ts-ignore
    const nodes: Node[] = Array.from(deserializedYDoc.getMap("nodes").values());
    console.log("nodes", nodes);
    setNodes(nodes.filter((n) => ids.includes(n.id)).map((n) => ({ ...n, selected: false })));
    // @ts-ignore
    setEdges(Array.from(deserializedYDoc.getMap("edges").values()));

    setDetailLoading(false);
  };

  useEffect(() => {
    if (reactFlowInstance && nodes.length > 0) {
      reactFlowInstance.fitView();
    }
  }, [reactFlowInstance, nodes]);

  useEffect(() => {
    if (currentVersion) {
      console.log("loadVersion", currentVersion.pk);
      loadDetail();
    }
  }, [currentVersion]);

  return (
    <div className={styles.wrapper}>
      {isFetched && versions.length == 0 && (
        <div className={styles.noVersions}>
          No versions saved yet, they are saved every 5 minutes
        </div>
      )}
      {isLoading && (
        <div className={styles.loading}>
          Loading...
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      {detailLoading && (
        <div className={styles.detailLoading}>
          Loading version...
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      )}
      {currentVersion ? (
        <div className={styles.editor}>
          <div className={styles.editorButtons}>
            <div className={styles.toggle}>
              Show:{" "}
              <button
                className={classNames({ [styles.active]: currentView == "editor" })}
                onClick={() => setCurrentView("editor")}
              >
                Editor
              </button>{" "}
              <button
                className={classNames({ [styles.active]: currentView == "graph" })}
                onClick={() => setCurrentView("graph")}
              >
                Graph
              </button>
            </div>
            <button
              onClick={() => {
                if (currentVersionYdoc) {
                  const json = yDocToProsemirrorJSON(currentVersionYdoc, "default");
                  editor?.commands.setContent(json, true);
                  Array.from(currentVersionYdoc.getMap("edges").values()).forEach((e: Edge) =>
                    edgesMap?.set(e.id, e)
                  );
                  Array.from(currentVersionYdoc.getMap("nodes").values()).forEach((n: Node) =>
                    nodesMap?.set(n.id, n)
                  );
                  // currentVersion?.content?.nodes.forEach((n: Node) => nodesMap?.set(n.id, n));
                  // currentVersion?.content?.edges.forEach((e: Edge) => edgesMap?.set(e.id, e));
                  queryClient.invalidateQueries({ queryKey: ["pages", project?.id] });
                  queryClient.invalidateQueries({ queryKey: ["page-ids"] });
                }
              }}
            >
              Make current version
            </button>
          </div>
          <div
            className={styles.reactflowWrapper}
            style={{ display: currentView == "graph" ? "block" : "none" }}
          >
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              minZoom={0.1}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
            >
              <Controls />
              <Background gap={12} size={1} />
            </ReactFlow>
          </div>
          <EditorContent
            editor={readOnlyEditor}
            style={{ display: currentView == "editor" ? "block" : "none" }}
          />
        </div>
      ) : (
        <></>
      )}
      <div className={styles.versions}>
        {versions?.map((version, i) => (
          <div
            className={classNames(styles.version, {
              [styles.active]: currentVersion?.pk == version.pk,
            })}
            key={`${id}-version-${i}`}
            onClick={() => {
              setCurrentVersion(version);
            }}
          >
            <div className={styles.versionDate}>
              <span>{formatTimeAgo(version.created_at)}</span>
              <span>{format(parseISO(version.created_at), "HH:mm - MMM dd yyyy")}</span>
            </div>
            <div className={styles.buttons}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setCurrentVersion(version);
                }}
              >
                View
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
